import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Routes :
 *   /         → Dashboard (auth requise) — landing page
 *   /planner  → Planner journée/semaine + tâches (auth)
 *   /gantt    → Gantt (auth)
 *   /editeurs → Fonctionnalités GedMouv activées par éditeur (auth)
 *   /jira     → Jira kanban (auth)
 *   /planif   → Planif admin (auth)
 *   /settings → Réglages (auth)
 *   /login    → Login form (public, redirige vers / si déjà auth)
 *   /vote/:t  → Page de vote externe (PUBLIC, token-based)
 *
 * L'appli est servie à la racine du domaine (base `/` dans vite.config.ts) :
 * les paths ci-dessous sont les URLs réelles. Elle a vécu sous /v2/ pendant
 * la cohabitation avec la V1 — Caddy redirige encore ces anciennes URLs.
 */
export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/vote/:token',
      name: 'vote',
      component: () => import('@/views/VoteView.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/accept-invite/:token',
      name: 'accept-invite',
      component: () => import('@/views/AcceptInviteView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('@/layouts/AppLayout.vue'),
      children: [
        { path: '',         name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
        { path: 'planner',  name: 'planner',  component: () => import('@/views/PlannerView.vue') },
        { path: 'gantt',    name: 'gantt',    component: () => import('@/views/GanttView.vue') },
        { path: 'editeurs', name: 'editeurs', component: () => import('@/views/EditeursView.vue') },
        { path: 'planif',   name: 'planif',   component: () => import('@/views/PlanifView.vue') },
        { path: 'jira',     name: 'jira',     component: () => import('@/views/JiraView.vue') },
        { path: 'settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
        { path: 'admin/users', name: 'admin-users', component: () => import('@/views/UsersAdminView.vue'), meta: { requireRole: 'ROLE_ADMIN' } },
        { path: 'admin/audit', name: 'admin-audit', component: () => import('@/views/AdminAuditView.vue'), meta: { requireRole: 'ROLE_ADMIN' } },
      ],
    },
    { path: '/:catchAll(.*)', redirect: '/' },
  ],
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const auth = useAuthStore()
  if (!auth.checked) await auth.fetchMe()

  if (to.meta.public) {
    // Page publique : si déjà auth + vient sur /login, redirige vers /
    if (to.name === 'login' && auth.isAuthenticated) return { name: 'dashboard' }
    return true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  /* Rôles restreints « Gantt seul » / « Editeurs seul » : confinés à leur
   * seule page. Toute autre route redirige vers l'une des pages autorisées. */
  if (auth.isGanttOnly || auth.isEditeursOnly) {
    const allowed = new Set<string>()
    if (auth.isGanttOnly) allowed.add('gantt')
    if (auth.isEditeursOnly) allowed.add('editeurs')
    if (!allowed.has(to.name as string)) {
      return { name: allowed.has('gantt') ? 'gantt' : 'editeurs' }
    }
  }
  /* Garde rôle : si la route exige un rôle, on vérifie. Sinon redirige
   * vers / (le user n'a probablement rien à faire là). */
  const required = (to.meta.requireRole as string | undefined)
  if (required && !(auth.user?.roles ?? []).includes(required)) {
    return { name: 'dashboard' }
  }
  return true
})
